// A borda vermelha de cada indicador avalia o valor exibido na visao atual (min/max do campo). O filtro
// de limites quebrados e resolvido no backend (brokenLimit/brokenLimitTypes) sobre a sugestao do calculo
// automatico, em todas as visoes. O aviso sinaliza, com o filtro aplicado, o indicador em que os dois
// criterios divergem. O front nao avalia limite aqui: so compara o que ja recebe.

export type BrokenLimitCellType = 'MARGIN' | 'COMPETITIVENESS' | 'PRICE_LIST';

export type BrokenLimitWarning = 'BROKEN_SUGGESTION' | 'UNBROKEN_SUGGESTION';

// true = "Sugestao com limites quebrados", false = "Sugestao sem limites quebrados", null = sem filtro
export type BrokenLimitFilterValue = boolean | null;

type GetBrokenLimitWarningParams = {
  rowData: Record<string, unknown>;
  localBroken: boolean;
  type: BrokenLimitCellType;
  brokenLimitFilter: BrokenLimitFilterValue;
};

export const BROKEN_LIMIT_WARNING_TEXT: Record<BrokenLimitWarning, string> = {
  BROKEN_SUGGESTION: 'O valor exibido está dentro do limite, mas a sugestão do cálculo automático quebra o limite em alguma visão.',
  UNBROKEN_SUGGESTION: 'O valor exibido quebra o limite nesta visão, mas a sugestão do cálculo automático está dentro do limite.',
};

type BrokenLimitFilterState = {
  isActive: boolean;
  value: boolean;
};

export const getBrokenLimitFilterValue = ({ isActive, value }: BrokenLimitFilterState): BrokenLimitFilterValue => (isActive ? value : null);

export const API_LIMIT_TYPES_BY_CELL_TYPE: Record<BrokenLimitCellType, readonly string[]> = {
  MARGIN: ['MARGIN', 'OBJECTIVE_MARGIN'],
  COMPETITIVENESS: ['COMPETITIVENESS'],
  PRICE_LIST: ['PRICE_LIST'],
};

// Campo com o valor exibido de cada indicador. Sem valor (linha carregando), nao ha aviso (RF 5.1).
const VALUE_FIELD_BY_CELL_TYPE: Record<BrokenLimitCellType, string> = {
  MARGIN: 'novaMargem',
  COMPETITIVENESS: 'novaCompetitividade',
  PRICE_LIST: 'price',
};

const hasIndicatorValue = (rowData: Record<string, unknown>, type: BrokenLimitCellType): boolean => {
  const value = rowData[VALUE_FIELD_BY_CELL_TYPE[type]];

  return value !== null && value !== undefined;
};

const EDITED_PRICE_STATUSES: readonly string[] = ['EDITED', 'EDITADO'];

// Preco editado nunca olha o brokenLimit: vale so o min/max do campo.
const isEditedPrice = (rowData: Record<string, unknown>): boolean =>
  typeof rowData.priceStatus === 'string' && EDITED_PRICE_STATUSES.includes(rowData.priceStatus);

type ShouldPaintBrokenLimitParams = {
  rowData: Record<string, unknown>;
  localBroken: boolean;
  brokenLimitFilter: BrokenLimitFilterValue;
};

// Pinta o input pelo min/max do campo. Com "Sugestao sem limites quebrados" o input nao e pintado e a
// quebra aparece so pelo icone de aviso; preco editado pinta sempre, qualquer que seja o filtro.
export const shouldPaintBrokenLimit = ({ rowData, localBroken, brokenLimitFilter }: ShouldPaintBrokenLimitParams): boolean => {
  if (isEditedPrice(rowData)) return localBroken;
  if (brokenLimitFilter === false) return false;

  return localBroken;
};

const isSuggestionBroken = (rowData: Record<string, unknown>, type: BrokenLimitCellType): boolean => {
  const { brokenLimit, brokenLimitTypes } = rowData;

  if (brokenLimit !== true || !Array.isArray(brokenLimitTypes)) return false;

  return API_LIMIT_TYPES_BY_CELL_TYPE[type].some(apiType => brokenLimitTypes.includes(apiType));
};

export const getBrokenLimitWarning = ({ rowData, localBroken, type, brokenLimitFilter }: GetBrokenLimitWarningParams): BrokenLimitWarning | null => {
  if (brokenLimitFilter === null) return null;
  if (isEditedPrice(rowData)) return null;
  if (!hasIndicatorValue(rowData, type)) return null;

  if (brokenLimitFilter === false) return localBroken ? 'UNBROKEN_SUGGESTION' : null;

  // Nao exige limite configurado no valor exibido: na visao Cluster a linha mostra a loja representante,
  // que pode nao ter o limite que quebrou em outra loja do cluster (IT-5187).
  return !localBroken && isSuggestionBroken(rowData, type) ? 'BROKEN_SUGGESTION' : null;
};
